<script setup lang="ts">
/**
 * Square icon tile from the template's service cards: soft grey square with a
 * green Bootstrap icon inside. `round` gives the green circle variant used in
 * the content lists.
 *
 * Icon colour and size sit on the `<i>`, not the wrapper: the global
 * `:root span` rule (and `.sofax span`) repaint every span dark at 1rem, which
 * beat `text-white` / `text-accent` and left the icons dark.
 */
withDefaults(defineProps<{
  icon: string
  round?: boolean
  size?: 'sm' | 'md' | 'lg'
  /**
   * Reacts to the hover of an ancestor marked `.group`: the grey square fills
   * green and the icon turns white, so a whole card answers the pointer with
   * one gesture. Transforms are skipped under reduced motion.
   */
  interactive?: boolean
}>(), {
  size: 'lg',
})

const boxSizes = {
  sm: 'size-12',
  md: 'size-16',
  lg: 'size-20',
} as const

const iconSizes = {
  sm: 'text-xl',
  md: 'text-2xl',
  lg: 'text-3xl',
} as const
</script>

<template>
  <span
    class="inline-flex shrink-0 items-center justify-center transition-all duration-300"
    :class="[
      boxSizes[size],
      round ? 'rounded-full bg-accent' : 'rounded-[0.625rem] bg-surface dark:bg-gray-700',
      interactive && !round ? 'group-hover:bg-accent motion-safe:group-hover:-rotate-6 motion-safe:group-hover:scale-105 dark:group-hover:bg-accent' : '',
      interactive && round ? 'motion-safe:group-hover:scale-105' : '',
    ]"
  >
    <i
      class="bi leading-none transition-colors duration-300"
      :class="[
        icon,
        iconSizes[size],
        round ? 'text-white' : 'text-accent',
        interactive && !round ? 'group-hover:text-white' : '',
      ]"
    />
  </span>
</template>
